import {
  faBox,
  faCamera,
  faCameraAlt,
  faClipboard,
  faComment,
  faGasPump,
  faRoute,
  faSignature,
  faWater,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify'; // Assurez-vous d'avoir installé react-toastify
import useGetTourDetails from '../../../api/tours/useGetToursDetails';
import MainButton from '../../../components/agv-button';
import AgrivitechCheckbox from '../../../components/agv-checkbox/agv-checkbox';
import GenericModal from '../../../components/agv-generic-modal';
import DownloadableImage from '../../../components/agv-downloadable-image';
import Status from '../../../components/agv-status';
import AgvTextDropdown from '../../../components/agv-text-dropdown';
import AgvTimePickerDropdown from '../../../components/agv-time-picker-dropdown';
import AlimentTypeTag from '../../../components/fleet-aliment-type-tag/aliment-type-tag';
import { ALIMENT_TYPE_ENUM } from '../../../components/fleet-aliment-type-tag/aliment-type.enum';
import { BL_STATUS, PACKAGING_TYPE } from '../../../enums/bl.enum';
import { DELIVERY_DETAIL_TYPE } from '../../../enums/delivery-detail.enum';
import DOCUMENT_TYPE from '../../../enums/documents.enum';
import FUEL from '../../../enums/fuel.enum';
import { MODAL_SIZE, MODAL_SIZE_STYLE } from '../../../enums/modal-size.enum';
import { step_status } from '../../../enums/step-status.enum';
import { STEP_TYPE } from '../../../enums/step-type.enum';
import fleetApi from '../../../services/api.service';
import { computeRowForOneDeliveryDetail } from '../../../utils/silo.util';
import computeTourSubtitle from '../tour-util';
import { reserve_status } from '../../../enums/reserve-status.enum';
import { Link } from 'react-router-dom';
import { tour_status } from '../../../enums/tour-status';
import Products from '../../../components/fleet-silos-display';
import StepConsignmentsDisplay, {
  hasStepConsignments,
} from '../../../components/consignment/step-consignments-display';
import { isConsignmentPackagingEnabled } from '../../../utils/consignment.util';
import useGetPackaging from '../../../api/packaging/useGetPackaging';

const PACKAGING_REFERENTIAL_FILTERS = { limit: 500, offset: 0 };

function ModalTour({ tourSelected, show, onHide }) {
  const {
    details,
    error: detailsError,
    isLoading: isDetailsLoading,
    refetch, // Ajoutez refetch ici
  } = useGetTourDetails(tourSelected?.tour_id);

  const DEFAULT_DEPARTURE_TIME = '02:00';
  const DEFAULT_CLEANING = 'Non';

  const config = useSelector((state) => state.config);
  const features = useSelector((state) => state.features);

  // Référentiel packaging du client (code -> libellé humain) pour l'affichage
  // des palettes consignées multi-type dans les étapes (FLEETC-3001).
  const { data: packagingReferentialData } = useGetPackaging(
    PACKAGING_REFERENTIAL_FILTERS,
    0
  );
  const packagingLabelByCode = useMemo(() => {
    const map = {};
    for (const item of packagingReferentialData?.items ?? []) {
      if (item?.code) {
        map[item.code] = item.label;
      }
    }
    return map;
  }, [packagingReferentialData]);

  const [isLoading, setIsLoading] = useState(false);
  const [departureTime, setDepartureTime] = useState(DEFAULT_DEPARTURE_TIME);
  const [cleaning, setCleaning] = useState(DEFAULT_CLEANING);
  const [updateTourListOnClose, setUpdateTourListOnClose] = useState(false);

  const handleDownloadDocument = async (document_id) => {
    try {
      const response = await fleetApi.post(
        `/documents/${document_id}`,
        {},
        {
          responseType: 'blob',
        }
      );

      // Récupération du type de fichier depuis les headers de la réponse
      const contentType = response.headers['content-type'];
      const contentDisposition = response.headers['content-disposition'];

      // Extraction du nom de fichier depuis content-disposition s'il existe
      let filename = `document-${document_id}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Extension du fichier basée sur le content-type
      const extension = contentType.split('/')[1] || 'jpg';
      if (!filename.includes('.')) {
        filename = `${filename}.${extension}`;
      }

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Nettoyage
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Erreur lors du téléchargement du document:', error);
    }
  };

  // Réinitialisation à la fermeture
  useEffect(() => {
    if (!show) {
      setDepartureTime(DEFAULT_DEPARTURE_TIME);
      setCleaning(DEFAULT_CLEANING);
    }
  }, [show]);

  // Initialisation de l'heure à l'ouverture/changement de tournée
  useEffect(() => {
    if (show && details) {
      if (details?.status === tour_status.CREATED && details?.starting_date) {
        const date = new Date(details?.starting_date);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        setDepartureTime(`${hours}:${minutes}`);
      } else {
        setDepartureTime(DEFAULT_DEPARTURE_TIME);
      }
      setCleaning(DEFAULT_CLEANING);
    }
  }, [show, details]);

  const isButtonDisabled = () => {
    if (
      details?.status === tour_status.IMPORTED ||
      details?.status === tour_status.REJECTED
    ) {
      return isLoading || !departureTime || !cleaning;
    }

    // Pour CREATED : désactiver si l'heure n'a pas changé
    if (details?.status === tour_status.CREATED) {
      const initialHour = new Date(details?.starting_date).getHours();
      const initialMinute = new Date(details?.starting_date).getMinutes();
      const selectedHour = departureTime
        ? parseInt(departureTime.split(':')[0])
        : null;
      const selectedMinute = departureTime
        ? parseInt(departureTime.split(':')[1])
        : null;

      // Désactiver si l'heure et la minute sont identiques à l'heure et la minute initiales
      if (initialHour === selectedHour && initialMinute === selectedMinute) {
        return true;
      }
    }

    return isLoading;
  };

  const handleSendRequest = async (needToSendNotification) => {
    setIsLoading(true);

    try {
      const response = await fleetApi.post(
        `/tours/validate/${tourSelected.tour_id}`,
        {
          is_cleaning_mandatory: cleaning === 'Oui' ? 1 : 0,
          start_hour: departureTime,
          need_to_send_notification: needToSendNotification ? 1 : 0,
        }
      );

      if (response.status === 200) {
        toast.success('La tournée a été validée avec succès !');
        onHide(true);
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (error) {
      console.error('Erreur lors de la validation de la tournée:', error);
      toast.error(
        'Une erreur est survenue lors de la validation de la tournée. Veuillez réessayer.'
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const handleRejectRequest = async () => {
    setIsLoading(true);

    try {
      const response = await fleetApi.post(
        `/tours/reject/${tourSelected.tour_id}`
      );

      if (response.status === 200) {
        toast.success('La tournée a été rejetée avec succès !');
        onHide(true);
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (error) {
      console.error('Erreur lors du rejet de la tournée:', error);
      toast.error(
        'Une erreur est survenue lors du rejet de la tournée. Veuillez réessayer.'
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  const computeStepIcon = (step) => {
    const deliveryDetails = step?.bls;

    if (step?.client_id === 6) {
      const itemsTypes = deliveryDetails?.flatMap((el) =>
        el?.items?.map((item) => item?.delivery_type)
      );

      const hasItemToDeliver = itemsTypes?.some(
        (type) => type === DELIVERY_DETAIL_TYPE.LIVRAISON
      );
      const hasItemToRetake = itemsTypes?.some(
        (type) => type === DELIVERY_DETAIL_TYPE.REPRISE
      );

      if (hasItemToDeliver && hasItemToRetake) {
        return STEP_TYPE.properties[STEP_TYPE['LIVRAISON-REPRISE']]?.icon;
      } else if (hasItemToDeliver) {
        return STEP_TYPE.properties[STEP_TYPE.LIVRAISON]?.icon;
      } else if (hasItemToRetake) {
        return STEP_TYPE.properties[STEP_TYPE.REPRISE]?.icon;
      } else {
        console.error('No matching delivery type found');
        return STEP_TYPE.properties[STEP_TYPE.LIVRAISON]?.icon;
      }
    }

    const types = deliveryDetails?.map((el) => el?.delivery_type);

    if (!types) {
      console.error('Types is undefined or empty');
      return null;
    }

    if (types.includes(DELIVERY_DETAIL_TYPE.TRANSVIDAGE)) {
      return STEP_TYPE.properties[STEP_TYPE.TRANSVIDAGE]?.icon;
    } else if (types.includes(DELIVERY_DETAIL_TYPE.GARDER)) {
      return STEP_TYPE.properties[STEP_TYPE.GARDER]?.icon;
    } else if (types.includes(DELIVERY_DETAIL_TYPE['TRANSFERT-SILO'])) {
      return STEP_TYPE.properties[STEP_TYPE['TRANSFERT-SILO']]?.icon;
    } else if (
      (types.includes(DELIVERY_DETAIL_TYPE.LIVRAISON) ||
        types.includes(DELIVERY_DETAIL_TYPE['LIVRAISON-VIA-TRANSFERT-SILO'])) &&
      types.includes(DELIVERY_DETAIL_TYPE.REPRISE)
    ) {
      return STEP_TYPE.properties[STEP_TYPE['LIVRAISON-REPRISE']]?.icon;
    } else if (
      types.includes(DELIVERY_DETAIL_TYPE.LIVRAISON) ||
      types.includes(DELIVERY_DETAIL_TYPE['LIVRAISON-VIA-TRANSFERT-SILO'])
    ) {
      return STEP_TYPE.properties[STEP_TYPE.LIVRAISON]?.icon;
    } else if (types.includes(DELIVERY_DETAIL_TYPE.REPRISE)) {
      return STEP_TYPE.properties[STEP_TYPE.REPRISE]?.icon;
    } else {
      console.error('No matching delivery type found');
      return STEP_TYPE.properties[STEP_TYPE.LIVRAISON]?.icon;
    }
  };

  const formatAddress = (address) => {
    if (!address) {
      return 'Adresse non disponible';
    }

    const { address_number, street, city, zipcode } = address;

    const formattedStreet = address_number
      ? `${address_number} ${street}`
      : street;
    const formattedAddress = [formattedStreet, city, zipcode]
      .filter(Boolean)
      .join(', ');

    return formattedAddress || 'Adresse non disponible';
  };

  const washingTime = details?.washings?.reduce((accumulator, currentValue) => {
    return accumulator + currentValue?.duration_in_seconds;
  }, 0);

  const renderKilometerDetails = () => {
    if (
      !details?.inputted_kilometers &&
      !details?.last_kilometers &&
      !features?.TOUR_END?.END_MILEAGE?.enabled &&
      !details?.last_kilometers
    ) {
      return null;
    }

    return (
      <>
        <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
        <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
          <div className="list-item-title tw-text-text-dark-high-importance">
            Kilomètres début de tournée
          </div>
          <div className="list-item-title tw-text-gray-600">
            {details?.last_kilometers ? details.last_kilometers : '0'} km
          </div>
        </div>
        <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
        <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
          <div className="list-item-title tw-text-text-dark-high-importance">
            Kilomètres fin de tournée
          </div>
          <div className="list-item-title tw-text-gray-600">
            {details?.inputted_kilometers ? details.inputted_kilometers : '0'}{' '}
            km
          </div>
        </div>
        <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
        <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
          <div className="list-item-title tw-text-text-dark-high-importance">
            Distance
          </div>
          <div className="list-item-title tw-text-gray-600">
            {details?.inputted_kilometers && details.last_kilometers
              ? Number(details?.inputted_kilometers) -
                Number(details.last_kilometers)
              : '0'}{' '}
            km
          </div>
        </div>
        <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
        <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
          <div className="list-item-title tw-text-text-dark-high-importance">
            {'Impact CO₂'}
          </div>
          <div className="list-item-title tw-text-gray-600">
            {details?.impact_co2 ? `${details?.impact_co2.toFixed(2)}` : '0'} kg
          </div>
        </div>
      </>
    );
  };

  return (
    <GenericModal
      isOpen={show}
      setIsOpen={() => {
        if (updateTourListOnClose) {
          setUpdateTourListOnClose(false);
        }
        onHide(updateTourListOnClose);
      }}
      title={`Détails de la tournée ${details?.tour_key || tourSelected?.tour_key}`}
      size={MODAL_SIZE_STYLE[MODAL_SIZE.XXL]}
      padding={'tw-px-4'}
    >
      <section className="tw-self-stretch tw-flex tw-flex-col tw-items-stretch tw-justify-start tw-gap-6 tw-pt-8 tw-overflow-y-auto tw-flex-auto">
        <div className="tw-flex-1 tw-flex tw-flex-col tw-justify-start tw-max-w-full">
          <div className="tw-flex tw-flex-row tw-items-start tw-justify-between tw-w-full mq750:tw-flex-wrap">
            <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-px-0 tw-pb-0 tw-text-text-dark-high-importance">
              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-shrink-0">
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-px-0 tw-pb-0">
                  <div className="tw-w-6 tw-h-6 tw-relative tw-flex tw-items-center">
                    <FontAwesomeIcon
                      icon={faRoute}
                      className="tw-text-icon-gray"
                    />
                  </div>
                </div>
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                  <div className="tw-relative tw-font-medium tw-font-work tw-text-sm">
                    <span>Tour {details ? details?.tour_key : '-'}</span>
                  </div>
                  <div className="list-item-title tw-text-gray-600">
                    {computeTourSubtitle(features, details)}{' '}
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-4 tw-shrink-0 tw-max-w-full tw-text-smi mq450:tw-flex-wrap">
              {(details?.status === tour_status.IMPORTED ||
                details?.status === tour_status.REJECTED ||
                details?.status === tour_status.CREATED) &&
                features?.OTHERS?.BACKOFFICE?.canSetStartingHourForTours && (
                  <>
                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                      <div className="list-item-title text-text-dark-high-importance">
                        Heure départ
                      </div>
                      <div className="list-item-title tw-text-gray-600">
                        <AgvTimePickerDropdown
                          value={departureTime}
                          onChange={(value) => setDepartureTime(value)}
                          defaultValue={departureTime}
                        />
                      </div>
                    </div>
                  </>
                )}
              {(details?.status === tour_status.IMPORTED ||
                details?.status === tour_status.REJECTED) &&
                features?.OTHERS?.BACKOFFICE?.canSetCleaningNeededForTours && (
                  <>
                    <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                      <div className="list-item-title text-text-dark-high-importance">
                        Nettoyage
                      </div>
                      <div className="list-item-title tw-text-gray-600">
                        <AgvTextDropdown
                          defaultValue={cleaning}
                          options={[
                            { value: 'Oui', name: 'Oui' },
                            { value: 'Non', name: 'Non' },
                          ]}
                          onChange={(value) => setCleaning(value)}
                        />
                      </div>
                    </div>
                    <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
                  </>
                )}

              <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                <div className="list-item-title tw-text-text-dark-high-importance">
                  <Status
                    status={tour_status.properties[details?.status]?.color}
                    text={tour_status.properties[details?.status]?.display_name}
                  />
                </div>
              </div>

              {(details?.status === tour_status.IMPORTED ||
                details?.status === tour_status.REJECTED ||
                details?.status === tour_status.CREATED) &&
                features?.OTHERS?.UPLOAD?.tourRequiresBackofficeValidation && (
                  <>
                    <div className="tw-w-[1px] tw-h-4 tw-bg-black tw-opacity-20" />
                    {details?.status === tour_status.IMPORTED && (
                      <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                        <div className="list-item-title tw-text-gray-600">
                          <MainButton
                            style={'danger'}
                            text={isLoading ? 'Rejet en cours...' : 'Rejeter'}
                            onClick={handleRejectRequest}
                            disabled={isLoading}
                          >
                            {isLoading ? 'Rejet en cours...' : 'Rejeter'}
                          </MainButton>
                        </div>
                      </div>
                    )}
                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                      <div className="list-item-title tw-text-gray-600">
                        <MainButton
                          style={''}
                          text={
                            isLoading
                              ? 'Envoi en cours...'
                              : 'Envoyer sans notification'
                          }
                          onClick={() => handleSendRequest(false)}
                          disabled={isButtonDisabled()}
                        >
                          {isLoading
                            ? 'Envoi en cours...'
                            : 'Envoyer sans notification'}
                        </MainButton>
                      </div>
                    </div>
                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                      <div className="list-item-title tw-text-gray-600">
                        <MainButton
                          style={''}
                          text={isLoading ? 'Envoi en cours...' : 'Envoyer'}
                          onClick={() => handleSendRequest(true)}
                          disabled={isButtonDisabled()}
                        >
                          {isLoading ? 'Envoi en cours...' : 'Envoyer'}
                        </MainButton>
                      </div>
                    </div>
                  </>
                )}
            </div>
          </div>
        </div>

        <div className="tw-h-[1px] tw-w-full tw-bg-black tw-opacity-20" />

        <div className="tw-pb-16 tw-gap-6 tw-overflow-y-auto tw-flex tw-flex-col tw-items-stretch tw-justify-start tw-relative tw-max-w-full tw-text-smi tw-text-text-dark-high-importance">
          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-max-w-full tw-text-gray-600">
            <div className="tw-flex tw-flex-col tw-pb-6 tw-text-base tw-text-text-dark-high-importance">
              <div className="tw-relative tw-font-medium">
                Détails de la tournée
              </div>
              <div className="tw-relative tw-text-smi tw-leading-[15px] tw-text-text-dark-medium-importance">
                Informations générales et métriques de la tournée
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-1">
              {details?.vehicle_total_loaded_weight != undefined && (
                <div className="list-item-title tw-text-gray-600">
                  Poids restant :{' '}
                  {details?.vehicle_total_loaded_weight != undefined
                    ? details?.total_loaded_weight &&
                      details?.vehicle_total_loaded_weight !=
                        details?.total_loaded_weight
                      ? `${details?.vehicle_total_loaded_weight?.toFixed(3)} t. / ${details?.total_loaded_weight?.toFixed(3)} t.`
                      : `${details?.vehicle_total_loaded_weight?.toFixed(3)} t.`
                    : '-'}
                </div>
              )}
              <div className="list-item-title tw-text-gray-600">
                Étapes :{' '}
                {details?.finalized_step_count != undefined &&
                details?.total_step_count != undefined
                  ? `${details?.finalized_step_count} sur ${details?.total_step_count}`
                  : '-'}
              </div>

              {(details?.inputted_kilometers ||
                details?.last_kilometers ||
                features?.TOUR_END?.END_MILEAGE?.enabled) && (
                <>
                  <div className="list-item-title tw-text-gray-600">
                    Kilomètres début de tournée :{' '}
                    {details?.last_kilometers ? details.last_kilometers : '0'}{' '}
                    km
                  </div>
                  <div className="list-item-title tw-text-gray-600">
                    Kilomètres fin de tournée :{' '}
                    {details?.inputted_kilometers
                      ? details.inputted_kilometers
                      : '0'}{' '}
                    km
                  </div>
                  <div className="list-item-title tw-text-gray-600">
                    Distance :{' '}
                    {details?.inputted_kilometers && details.last_kilometers
                      ? Number(details?.inputted_kilometers) -
                        Number(details.last_kilometers)
                      : '0'}{' '}
                    km
                  </div>
                  <div className="list-item-title tw-text-gray-600">
                    Impact CO₂ :{' '}
                    {details?.impact_co2
                      ? `${details?.impact_co2.toFixed(2)}`
                      : '0'}{' '}
                    kg
                  </div>
                </>
              )}

              {features?.TOUR_MANAGEMENT?.FUEL_CONSIGNMENT?.enabled &&
                details?.tour_refueling && (
                  <>
                    <div className="list-item-title tw-text-gray-600">
                      Diesel total :{' '}
                      {details?.tour_refueling?.find(
                        (el) => el.fuel === FUEL.DIESEL
                      )?.value ?? 0}{' '}
                      L
                    </div>
                    <div className="list-item-title tw-text-gray-600">
                      Consommation diesel :{' '}
                      {details?.tour_refueling?.find(
                        (el) => el.fuel === FUEL.DIESEL
                      )?.value &&
                      details?.inputted_kilometers &&
                      details.last_kilometers
                        ? (details.tour_refueling.find(
                            (el) => el.fuel === FUEL.DIESEL
                          ).value /
                            (details?.inputted_kilometers &&
                            details.last_kilometers
                              ? Number(details?.inputted_kilometers) -
                                Number(details.last_kilometers)
                              : 0)) *
                          100
                        : '0'}{' '}
                      L / 100 km
                    </div>
                    <div className="list-item-title tw-text-gray-600">
                      GNC total :{' '}
                      {details?.tour_refueling?.find(
                        (el) => el.fuel === FUEL.GNC
                      )?.value ?? 0}{' '}
                      kg
                    </div>
                    <div className="list-item-title tw-text-gray-600">
                      Consommation GNC :{' '}
                      {details?.tour_refueling?.find(
                        (el) => el.fuel === FUEL.GNC
                      )?.value &&
                      details?.inputted_kilometers &&
                      details.last_kilometers
                        ? (details.tour_refueling.find(
                            (el) => el.fuel === FUEL.GNC
                          ).value /
                            (details?.inputted_kilometers &&
                            details.last_kilometers
                              ? Number(details?.inputted_kilometers) -
                                Number(details.last_kilometers)
                              : 0)) *
                          100
                        : '0'}{' '}
                      kg / 100 km
                    </div>
                    <div className="list-item-title tw-text-gray-600">
                      AdBlue :{' '}
                      {details?.tour_refueling?.find(
                        (el) => el.fuel === FUEL.ADBLUE
                      )?.value ?? 'Non renseigné'}
                    </div>
                  </>
                )}

              {features?.TOUR_END?.WASHING_TIME?.enabled && (
                <div className="list-item-title tw-text-gray-600">
                  Temps total de nettoyage : {Math.floor(washingTime / 60)} min
                </div>
              )}

              {details?.tour_comments && (
                <div className="list-item-title tw-text-gray-600">
                  Commentaire : {details.tour_comments}
                </div>
              )}

              {details?.tour_documents?.length > 0 && (
                <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-[1px] tw-text-left tw-text-xs tw-text-primary tw-mt-2">
                  {details.tour_documents.map((el) => (
                    <div
                      key={`el_${el.id}`}
                      className="tw-relative [text-decoration:underline] tw-tracking-[0.02em]"
                    >
                      <a href={el.url} target="_blank" rel="noreferrer">
                        Télécharger{' '}
                        {el?.doc_type
                          ? DOCUMENT_TYPE.properties[el?.doc_type]?.pronoun
                          : 'la'}{' '}
                        {el?.doc_type
                          ? DOCUMENT_TYPE.properties[el?.doc_type]?.display_name
                          : 'DOCUMENT_TYPE'}
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* tour steps block */}
          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-max-w-full tw-text-gray-600">
            {/* section header */}
            <div className="tw-flex tw-flex-col tw-pb-6 tw-text-base tw-text-text-dark-high-importance">
              <div className="tw-relative tw-font-medium">Liste des étapes</div>
              <div className="tw-relative tw-text-smi tw-leading-[15px] tw-text-text-dark-medium-importance">
                Retrouvez la liste des étapes du tour
              </div>
            </div>
            {/* section table header */}
            <div className="tw-self-stretch tw-flex tw-flex-row tw-items-start tw-justify-start tw-pt-0 tw-px-0 tw-pb-4 tw-box-border tw-max-w-full tw-shrink-0 tw-text-right">
              <div className="tw-flex-1 tw-bg-whitesmoke-400 tw-flex tw-flex-row tw-flex-wrap tw-items-start tw-justify-start tw-py-2 tw-px-2 tw-shrink-0 [row-gap:20px]">
                <div className="tw-flex-1 tw-relative tw-text-text-dark-medium-importance tw-text-left tw-inline-block tw-min-w-[450px] tw-max-w-full">
                  Étape
                </div>

                <div className="tw-flex-1 tw-relative tw-inline-block tw-min-w-[82px]">
                  Statut(s)
                </div>
              </div>
            </div>
            {/* section content */}
            {details?.steps.map((step) => {
              const IconComponentStep = computeStepIcon(step);

              const allBlsArePresent =
                step?.bls?.every((bl) => bl.is_present) || false;
              const allBlsAreDropable =
                step?.bls?.every((bl) => bl.is_dropable) || false;

              // Spé Sévèal
              const showLdaInformation =
                config?.client_id === 1 &&
                step?.additional_datas?.is_eligible_for_lda === 1;

              const showSignatureIcon =
                features?.TOUR_STEP?.STEP_ACTIONS?.signingAttendance?.enabled &&
                !showLdaInformation;
              const showCameraIcon =
                features?.TOUR_STEP?.STEP_ACTIONS?.mandatoryAttendance
                  ?.enabled && !showLdaInformation;

              const isStepNotValid = step.is_handled === 0;

              let stepReceivers;
              const anyBlWithReceiverFinal =
                step?.bls?.some(
                  (bl) => bl?.additional_data?.receiver_final?.length > 0
                ) || false;

              if (anyBlWithReceiverFinal) {
                stepReceivers = [
                  ...new Set(
                    step.bls
                      .filter(
                        (bl) => bl?.additional_data?.receiver_final?.length > 0
                      )
                      .map((bl) => bl?.additional_data?.receiver_final)
                  ),
                ];
              } else {
                stepReceivers = [step?.end_address?.name];
              }

              return (
                <div
                  key={`${step.step_id}`}
                  className={`tw-self-stretch tw-flex tw-flex-col tw-items-end tw-justify-start tw-pt-4 tw-px-0 tw-pb-4 tw-gap-4 tw-shrink-0 tw-max-w-full tw-text-sm tw-border-solid tw-border-background tw-border-0 tw-border-b ${
                    isStepNotValid ? 'tw-opacity-50' : ''
                  }`}
                >
                  {/* main step row */}
                  <div className="tw-self-stretch tw-flex tw-flex-row tw-items-start tw-justify-start [row-gap:20px] tw-max-w-full tw-text-right tw-text-smi tw-text-text-dark-high-importance">
                    <div className="tw-flex-1 tw-overflow-hidden tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-[2.1px] tw-min-w-[450px] tw-max-w-full tw-text-left">
                      <div className="tw-flex tw-flex-row tw-items-start tw-justify-start tw-gap-[16px] tw-text-sm">
                        <div className="tw-flex tw-flex-row tw-items-start tw-justify-start">
                          <div className="tw-h-4 tw-w-[50px] tw-relative">
                            {/* <FontAwesomeIcon
                                icon={faTruck}
                                className="tw-text-icon-gray tw-absolute tw-top-0 tw-left-0 tw-w-6 tw-h-6 tw-text-2xl"
                              /> */}
                            <IconComponentStep />
                          </div>
                          <div className="tw-flex tw-flex-col tw-items-start tw-justify-start">
                            <div className="tw-relative tw-font-semibold tw-flex tw-gap-2 tw-text-sm tw-items-center">
                              <div className="">
                                {step?.step_key
                                  ? `Étape ${step.step_key}`
                                  : 'STEP_NAME'}
                              </div>
                              {features?.OTHERS?.BACKOFFICE
                                ?.displayReceiversOnStepCard && (
                                <>
                                  <span>-</span>
                                  <div className="tw-text-sm">
                                    {stepReceivers?.join?.(', ')}
                                  </div>
                                </>
                              )}
                            </div>
                            {!features?.OTHERS?.BACKOFFICE
                              ?.displayReceiversOnStepCard && (
                              <div className="list-item-title">
                                {`Destinataire${stepReceivers?.length > 1 ? 's' : ''} : ${stepReceivers?.join?.(', ')}`}
                              </div>
                            )}
                            <div className="list-item-title">
                              {formatAddress(step?.end_address)}
                            </div>
                            {features?.TOUR_STEP?.STEP_ACTIONS
                              ?.mandatoryAttendance?.receivers?.enabled &&
                              step?.bls?.some(
                                (bl) =>
                                  bl?.additional_data?.receivers?.length > 0
                              ) && (
                                <div className="tw-flex tw-flex-col tw-items-center tw-justify-start tw-gap-[8px]">
                                  Receveurs :{' '}
                                  {step?.bls
                                    ?.find(
                                      (bl) =>
                                        bl?.additional_data?.receivers?.length >
                                        0
                                    )
                                    ?.additional_data?.receivers?.join?.(', ')}
                                </div>
                              )}
                            {features?.TOUR_STEP?.DISPLAY_ETA?.enabled && (
                              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-[8px]">
                                <div className="tw-relative list-item-title">
                                  Attendu au plus tard
                                </div>
                                <div className="polygon"></div>
                                <div className="tw-relative list-item-title">
                                  {step?.estimated_delivery
                                    ? moment(step?.estimated_delivery).format(
                                        'D MMMM YYYY[,] HH:mm'
                                      )
                                    : ''}
                                </div>
                              </div>
                            )}
                            <div className="list-item-title">
                              Poids total : {step?.total_weight}{' '}
                              {step?.weight_unit ? step?.weight_unit : 'KG'}
                            </div>
                            {showLdaInformation && (
                              <>
                                <div className="list-item-title">
                                  {step?.additional_datas
                                    ?.is_eligible_for_lda === 1
                                    ? `Identifiant LDA : ${step?.additional_datas?.lda_external_id}`
                                    : ''}
                                </div>
                                <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-[8px]">
                                  <div className="tw-flex-1 tw-relative tw-text-smi tw-tracking-[-0.02em] tw-text-text-dark-high-importance tw-min-w-[129px] tw-shrink-0">
                                    {showLdaInformation && (
                                      <FontAwesomeIcon
                                        icon={faBox}
                                        className={
                                          'tw-text-main tw-w-5 tw-h-5 tw-ml-2'
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              </>
                            )}

                            {(showSignatureIcon || showCameraIcon) && (
                              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-[8px]">
                                <div className="tw-flex-1 tw-relative tw-text-smi tw-tracking-[-0.02em] tw-text-text-dark-high-importance tw-min-w-[129px] tw-shrink-0">
                                  {showSignatureIcon && (
                                    <FontAwesomeIcon
                                      icon={faSignature}
                                      className={`${allBlsArePresent ? 'tw-text-main' : 'tw-text-icon-gray'} tw-w-5 tw-h-5 tw-ml-2`}
                                    />
                                  )}
                                  {showCameraIcon && (
                                    <FontAwesomeIcon
                                      icon={faCameraAlt}
                                      className={`${allBlsAreDropable ? 'tw-text-main' : 'tw-text-icon-gray'} tw-w-5 tw-h-5 tw-ml-2`}
                                    />
                                  )}
                                </div>
                              </div>
                            )}

                            {features?.TOUR_STEP?.MILEAGE_CONSIGNATION
                              ?.enabled && (
                              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-[8px]">
                                <div className="tw-flex-1 tw-relative tw-text-smi tw-tracking-[-0.02em] tw-text-text-dark-high-importance tw-min-w-[129px] tw-shrink-0">
                                  {`Kilométrage compteur : ${
                                    step?.inputted_kilometers
                                      ? step.inputted_kilometers + ' km'
                                      : 'Non communiqué'
                                  }`}
                                </div>
                              </div>
                            )}
                            {(step?.arrival_date || step?.departure_date) && (
                              <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-gap-[8px]">
                                <div className="tw-relative list-item-title">
                                  Arrivée - Départ
                                </div>
                                <div class="polygon"></div>

                                <div className="tw-flex-1 tw-relative tw-text-smi tw-tracking-[-0.02em] tw-text-text-dark-high-importance tw-min-w-[129px] tw-shrink-0 tw-w-full">
                                  {`${
                                    step?.arrival_date
                                      ? moment(step?.arrival_date).format(
                                          'HH[h]mm'
                                        )
                                      : 'Non communiqué'
                                  } - ${
                                    step?.departure_date
                                      ? moment(step?.departure_date).format(
                                          'HH[h]mm'
                                        )
                                      : 'Non communiqué'
                                  }`}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="tw-flex-1 tw-flex tw-flex-col tw-items-end tw-justify-start tw-gap-[4px] tw-text-center tw-text-xs tw-text-text-dark-medium-importance">
                      {isStepNotValid ? (
                        <Status
                          status={
                            step_status.properties[step_status.NOT_VALID]?.style
                          }
                          text={
                            step_status.properties[step_status.NOT_VALID]
                              ?.display_name
                          }
                          style={'tw-my-1'}
                        />
                      ) : (
                        <Status
                          status={step_status.properties[step.status]?.style}
                          text={
                            step_status.properties[step.status]?.display_name
                          }
                          style={'tw-my-1'}
                        />
                      )}

                      {step?.additional_datas?.need_to_disinfect ? (
                        <div className="tw-rounded-3xs tw-bg-goldenrod tw-flex tw-flex-row tw-items-start tw-justify-start tw-py-px tw-px-[7px] tw-whitespace-nowrap tw-text-black tw-border-[1px] tw-border-solid tw-border-dimgray-200">
                          <div className="tw-relative tw-font-medium tw-inline-block ">
                            Risque sanitaire
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {isConsignmentPackagingEnabled(features) &&
                    hasStepConsignments(step) && (
                      <StepConsignmentsDisplay
                        step={step}
                        packagingLabels={packagingLabelByCode}
                      />
                    )}

                  {step?.documents?.length > 0 && (
                    <div className="tw-flex tw-flex-col tw-pl-[50px] tw-items-start tw-justify-start tw-gap-3 tw-w-full">
                      <div className="tw-relative tw-tracking-[-0.02em] tw-font-medium">
                        Documents associées
                      </div>
                      <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-[1px] tw-text-left tw-text-xs tw-text-primary">
                        {step?.documents?.map?.((el) => (
                          <div
                            key={`el_${el.id}`}
                            className="tw-relative [text-decoration:underline] tw-tracking-[0.02em]"
                          >
                            <a href={el.url} target="_blank" rel="noreferrer">
                              Télécharger{' '}
                              {el?.doc_type
                                ? DOCUMENT_TYPE.properties[el?.doc_type]
                                    ?.pronoun
                                : 'la'}{' '}
                              {el?.doc_type
                                ? DOCUMENT_TYPE.properties[el?.doc_type]
                                    ?.display_name
                                : 'DOCUMENT_TYPE'}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* comments */}
                  <div className="tw-flex tw-flex-col tw-pl-[50px] tw-items-start tw-justify-start tw-gap-3 tw-w-full">
                    <div className="tw-relative tw-tracking-[-0.02em] tw-font-medium">
                      Commentaire itinéraire
                    </div>
                    <div className="tw-self-stretch tw-rounded-[3px] tw-bg-whitesmoke-100 tw-box-border tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-justify-start tw-py-3.5 tw-pr-3.5 tw-pl-4 tw-gap-[16px] tw-max-w-full tw-text-smi tw-text-text-dark-medium-importance tw-border-[1px] tw-border-solid tw-border-border-primary-strong">
                      <div className="tw-flex tw-flex-col tw-items-start tw-justify-start ">
                        <FontAwesomeIcon
                          icon={faComment}
                          className="tw-text-icon-gray tw-w-4 tw-h-4"
                        />
                      </div>
                      <p className="tw-flex-1 tw-relative tw-tracking-[-0.02em] tw-mb-0 tw-leÉing-4">
                        {step?.end_address?.address_comments
                          ? step?.end_address?.address_comments
                          : "Pas de commentaire d'itinéraire"}
                      </p>
                    </div>
                    <div className="tw-relative tw-tracking-[-0.02em] tw-font-medium">
                      Commentaire d'étape
                    </div>
                    <div className="tw-self-stretch tw-rounded-[3px] tw-bg-whitesmoke-100 tw-box-border tw-flex tw-flex-row tw-flex-wrap tw-items-center tw-justify-start tw-py-3.5 tw-pr-3.5 tw-pl-4 tw-gap-[16px] tw-max-w-full tw-text-smi tw-text-text-dark-medium-importance tw-border-[1px] tw-border-solid tw-border-border-primary-strong">
                      <div className="tw-flex tw-flex-col tw-items-start tw-justify-start ">
                        <FontAwesomeIcon
                          icon={faComment}
                          className="tw-text-icon-gray tw-w-4 tw-h-4"
                        />
                      </div>
                      <p className="tw-flex-1 tw-relative tw-tracking-[-0.02em] tw-mb-0 tw-leÉing-4">
                        {step?.comments
                          ? step?.comments
                          : 'Pas de commentaire pour cette étape'}
                      </p>
                    </div>
                  </div>
                  <div className="tw-flex tw-flex-col tw-pl-[50px] tw-items-start tw-justify-start tw-gap-3 tw-w-full">
                    <div className="tw-relative tw-tracking-[-0.02em] tw-font-medium">
                      Bons de livraison
                    </div>
                    <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
                      {step?.bls?.map?.((bl) => {
                        const IconComponent =
                          DELIVERY_DETAIL_TYPE.properties[bl?.delivery_type]
                            .icon;
                        return (
                          <div
                            key={`${bl?.delivery_detail_id}`}
                            className="tw-self-stretch tw-rounded-10xs tw-bg-whitesmoke-100 tw-box-border tw-flex tw-flex-row tw-flex-wrap tw-items-start tw-justify-between tw-py-2 tw-pr-[17px] tw-pl-[15px] tw-max-w-full tw-gap-[20px] tw-text-smi tw-text-text-dark-high-importance tw-border-[1px] tw-border-solid tw-border-border-primary-strong"
                          >
                            <div className="tw-flex tw-flex-row tw-items-start tw-justify-start tw-gap-2">
                              <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-pt-2 tw-px-0 tw-pb-0">
                                <IconComponent className="tw-text-icon-gray tw-w-8 tw-h-8" />
                              </div>
                              <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-3">
                                <div className="tw-flex tw-flex-row tw-items-start">
                                  <div className="tw-flex tw-flex-row tw-items-start tw-justify-start tw-gap-3">
                                    <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-0.5">
                                      <div className="tw-relative tw-font-medium tw-text-sm">
                                        {bl?.delivery_detail_key
                                          ? `BL n° ${bl.delivery_detail_key}`
                                          : 'DELIVERY_DETAIL_KEY'}{' '}
                                        {bl?.additional_data?.receiver_final &&
                                        bl?.additional_data?.receiver_final !==
                                          step?.end_address?.name
                                          ? `(${bl?.additional_data?.receiver_final})`
                                          : ''}
                                      </div>
                                      {bl?.status && (
                                        <Status
                                          status={
                                            BL_STATUS.properties[bl.status]
                                              .style
                                          }
                                          text={
                                            BL_STATUS.properties[bl.status]
                                              ?.display_name
                                          }
                                          style={'tw-my-1'}
                                        />
                                      )}
                                      <div className="tw-relative tw-text-text-dark-medium-importance list-item-title">
                                        {!features?.OTHERS?.BACKOFFICE
                                          ?.displayReceiversOnStepCard
                                          ? bl?.additional_data?.receiver_final
                                            ? bl?.additional_data
                                                ?.receiver_final
                                            : step?.end_address?.name ||
                                              'STEP_ADDRESS_NAME'
                                          : ''}
                                      </div>
                                      {bl?.reserves?.map?.((reserve) => (
                                        <Link
                                          to={
                                            '/reserves?query=' +
                                            reserve?.uuid +
                                            '&redirected=true'
                                          }
                                          key={'reserve_id_' + reserve.id}
                                        >
                                          Aller à la réserve #{reserve.id}
                                        </Link>
                                      ))}
                                    </div>
                                    <div className="tw-h-[33px] tw-flex tw-flex-col tw-items-center tw-justify-start">
                                      <img
                                        className="tw-w-[13.6px] tw-h-[13.5px] tw-relative"
                                        loading="lazy"
                                        alt=""
                                        src="/icon-akartrianglealertfill.svg"
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-4 tw-text-right tw-text-xs tw-text-primary tw-w-full">
                                  {features?.OTHERS?.BACKOFFICE
                                    ?.displayProofPhotosInline &&
                                    bl?.documents?.some?.(
                                      (doc) =>
                                        doc?.doc_type ===
                                        DOCUMENT_TYPE.PROOF_PHOTO
                                    ) && (
                                      <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-gap-2 tw-w-full">
                                        {bl?.documents
                                          ?.filter?.(
                                            (el) =>
                                              el?.doc_type ===
                                              DOCUMENT_TYPE.PROOF_PHOTO
                                          )
                                          ?.map?.((el) => (
                                            <DownloadableImage
                                              key={`el_${el.id}`}
                                              imageUrl={el.url}
                                              imageAlt="Proof photo"
                                              onDownload={() =>
                                                handleDownloadDocument(el.id)
                                              }
                                            />
                                          ))}
                                      </div>
                                    )}
                                  {bl?.documents
                                    ?.filter?.(
                                      (el) =>
                                        !features?.OTHERS?.BACKOFFICE
                                          ?.displayProofPhotosInline ||
                                        el?.doc_type !==
                                          DOCUMENT_TYPE.PROOF_PHOTO
                                    )
                                    ?.map?.((el) => (
                                      <div
                                        key={`el_${el.id}`}
                                        className="tw-relative [text-decoration:underline] tw-tracking-[0.02em]"
                                      >
                                        <a
                                          href={el.url}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          Télécharger{' '}
                                          {el?.doc_type
                                            ? DOCUMENT_TYPE.properties[
                                                el?.doc_type
                                              ]?.pronoun
                                            : 'la'}{' '}
                                          {el?.doc_type
                                            ? DOCUMENT_TYPE.properties[
                                                el?.doc_type
                                              ]?.display_name
                                            : 'DOCUMENT_TYPE'}
                                        </a>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                            <div className="tw-flex tw-flex-row tw-items-center tw-justify-start tw-align-center tw-min-w-[450px] tw-gap-4">
                              <div className="tw-h-[53px] tw-w-[1px] tw-relative tw-bg-black tw-opacity-5" />
                              <div className="tw-flex tw-flex-col tw-gap-1.5 tw-align-stretch tw-w-full">
                                <Products
                                  bl={bl}
                                  features={features}
                                  client_id={config?.client_id}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Localisation des étapes block */}
          {false && (
            <div className="tw-flex tw-flex-col tw-pb-0 tw-text-base tw-text-text-dark-high-importance">
              {/* section header */}
              <div className="tw-flex tw-flex-col tw-pb-6 tw-text-base tw-text-text-dark-high-importance">
                <div className="tw-relative tw-font-medium">
                  Localisation des étapes
                </div>
                <div className="tw-relative tw-text-smi tw-leading-[15px] tw-text-text-dark-medium-importance">
                  Localisation des étapes de la tournées sur la carte
                </div>
              </div>
              {/* map */}
              <div className="tw-self-stretch tw-h-[300px] tw-rounded-3xs tw-bg-white tw-box-border tw-overflow-x-auto tw-shrink-0 tw-flex tw-flex-row tw-items-start tw-justify-start tw-pt-[68.9px] tw-px-[387px] tw-pb-[61px] tw-gap-[98.9px] tw-text-smi tw-text-white tw-font-montserrat tw-border-[1px] tw-border-solid tw-border-border-primary-strong mq1150:tw-gap-[49px] mq1150:tw-pl-[193px] mq1150:tw-pr-[193px] tw-box-border mq450:tw-pl-5 mq450:tw-pr-5 tw-box-border mq750:tw-gap-[25px] mq750:tw-pl-24 mq750:tw-pr-24">
                <div className="tw-h-[300px] tw-w-[1250px] tw-relative tw-rounded-3xs tw-bg-white tw-box-border tw-shrink-0 tw-hidden tw-border-[1px] tw-border-solid tw-border-border-primary-strong" />
                <img
                  className="tw-h-[272.9px] tw-w-[1226.5px] tw-relative tw-shrink-0 tw-object-contain tw-hidden"
                  alt=""
                  src="/groupe-de-masques-10@2x.png"
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </GenericModal>
  );
}

export default ModalTour;
